import VisibilityIcon from "@mui/icons-material/Visibility";
import {
	Box,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
} from "@mui/material";
import { useState } from "react";
import {
	Datagrid,
	DateField,
	EmailField,
	ExportButton,
	FilterButton,
	Link,
	List,
	SelectInput,
	TextField,
	TextInput,
	TopToolbar,
	useCreatePath,
	useRecordContext,
} from "react-admin";

const auditLogFilters = [
	<TextInput
		key="actorEmail"
		label="Actor Email"
		source="actorEmail"
		alwaysOn
	/>,
	<SelectInput
		key="action"
		label="Action"
		source="action"
		choices={[
			{ id: "create", name: "Create" },
			{ id: "update", name: "Update" },
			{ id: "delete", name: "Delete" },
			{ id: "restore", name: "Restore" },
			{ id: "ban", name: "Ban" },
			{ id: "unban", name: "Unban" },
			{ id: "role_change", name: "Role Change" },
		]}
	/>,
	<SelectInput
		key="resourceType"
		label="Resource Type"
		source="resourceType"
		choices={[
			{ id: "app", name: "App" },
			{ id: "partner", name: "Partner" },
			{ id: "referral_link", name: "Referral Link" },
			{ id: "user", name: "User" },
		]}
	/>,
	<TextInput key="resourceId" label="Resource ID" source="resourceId" />,
];

const ListActions = () => (
	<TopToolbar>
		<FilterButton />
		<ExportButton />
	</TopToolbar>
);

const JsonViewerButton = ({
	data,
	title,
}: {
	data: unknown;
	title: string;
}) => {
	const [open, setOpen] = useState(false);

	if (!data) return <span>-</span>;

	return (
		<>
			<IconButton size="small" onClick={() => setOpen(true)}>
				<VisibilityIcon fontSize="small" />
			</IconButton>
			<Dialog
				open={open}
				onClose={() => setOpen(false)}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>{title}</DialogTitle>
				<DialogContent>
					<Box
						component="pre"
						sx={{
							backgroundColor: "#f5f5f5",
							p: 2,
							borderRadius: 1,
							overflow: "auto",
							maxHeight: 400,
							fontSize: "0.875rem",
						}}
					>
						{JSON.stringify(data, null, 2)}
					</Box>
				</DialogContent>
			</Dialog>
		</>
	);
};

const resourceTypeToName: Record<string, string> = {
	app: "apps",
	partner: "partners",
	referral_link: "referral-links",
	user: "users",
};

const ResourceLinkField = (_props: { label?: string }) => {
	const record = useRecordContext();
	const createPath = useCreatePath();
	if (!record?.resourceId) return <span>-</span>;

	const resourceName = resourceTypeToName[record.resourceType];
	if (!resourceName) return <span>{record.resourceId}</span>;

	const path = createPath({
		resource: resourceName,
		id: record.resourceId,
		type: "show",
	});

	return (
		<Link to={path} style={{ color: "#1976d2", textDecoration: "none" }}>
			{record.resourceId}
		</Link>
	);
};

const PreviousStateField = () => {
	const record = useRecordContext();
	return (
		<JsonViewerButton data={record?.previousState} title="Previous State" />
	);
};

const NewStateField = () => {
	const record = useRecordContext();
	return <JsonViewerButton data={record?.newState} title="New State" />;
};

const ActionChip = () => {
	const record = useRecordContext();
	if (!record) return null;

	const colors: Record<string, string> = {
		create: "#4caf50",
		update: "#2196f3",
		delete: "#f44336",
		restore: "#ff9800",
		ban: "#9c27b0",
		unban: "#00bcd4",
		role_change: "#795548",
	};

	return (
		<Box
			sx={{
				display: "inline-block",
				px: 1,
				py: 0.5,
				borderRadius: 1,
				backgroundColor: colors[record.action] || "#757575",
				color: "white",
				fontSize: "0.75rem",
				fontWeight: 500,
				textTransform: "uppercase",
			}}
		>
			{record.action}
		</Box>
	);
};

export const AuditLogList = () => (
	<List
		filters={auditLogFilters}
		actions={<ListActions />}
		sort={{ field: "createdAt", order: "DESC" }}
		exporter={false}
	>
		<Datagrid bulkActionButtons={false}>
			<DateField source="createdAt" label="Time" showTime />
			<ActionChip />
			<TextField source="resourceType" label="Resource" />
			<ResourceLinkField label="Resource ID" />
			<EmailField source="actorEmail" label="Actor" />
			<PreviousStateField />
			<NewStateField />
			<TextField source="ipAddress" label="IP" emptyText="-" />
		</Datagrid>
	</List>
);
